import { createApp, nextTick } from 'vue';
import ElementPlus from 'element-plus';
import Translator from 'gettext-translator';
import { Compass2, CompassEnums } from '@sellsy/compass2';
import mitt from 'mitt';

import ErrorsHandler from 'Services/ErrorsHandler';
import Feature from 'Services/Feature';
import Env from 'Services/Env';
import Store from 'Services/Store';

import { initializeVueDirectives } from 'js/config/authenticated/VueDirectives.js';

/**
 * Application Kernel
 * Manages Vue application initialization and bootstrapping
 */
export default class Kernel {
    /**
     * @param {Object} config - Application configuration
     */
    constructor(config) {
        this.config = config;
        this.bootData = null;
    }

    /**
     * Setup and boot application
     * @param {Object} bootData - Backend injected data
     */
    async boot(bootData) {
        this.bootData = bootData;

        const app = createApp({
            components: this.config.components,
        });

        await this.bootCoreServices(app);

        const store = Store.getStates();

        await this.setupVuePlugins(app, store);

        await this.setupGlobalProperties(app, store);

        initializeVueDirectives(app);

        this.setupGlobalReferences(app);

        app.mount('#sellsy_app');

        this.bootLegacy();
    }

    /**
     * Setup Vue plugins (router, store, ElementPlus, Compass2)
     * @param {App} app - The Vue instance
     * @param {Object} store - The Vuex store instance
     */
    async setupVuePlugins(app, store) {
        if (this.config.router) {
            app.use(this.config.router);
        }
        app.use(store);

        const { default: locale } =
            window.App.lang === 'fr'
                ? await import('element-plus/es/locale/lang/fr')
                : await import('element-plus/es/locale/lang/en');
        app.use(ElementPlus, { locale });

        app.use(Compass2);
    }

    /**
     * Setup Vue application global properties
     * @param {App} app - The Vue instance
     * @param {Object} store - The Vuex store instance
     */
    async setupGlobalProperties(app, store) {
        const locale = window.App.locale.split('.')[0];
        let keyTranslations = {};
        try {
            keyTranslations = require(
                `../../../../public/ressources/locales/messages.${locale}.json`,
            );
        } catch {
            keyTranslations = require(`../../../../public/ressources/locales/messages.fr_FR.json`);
        }

        // Core services
        app.config.globalProperties.window = window;
        app.config.globalProperties.$ajax = window.SellsyJS.WebServiceCaller;
        app.config.globalProperties.$l10n = window.i18n;
        app.config.globalProperties.$t = new Translator(keyTranslations);
        app.config.globalProperties.$nextTick = nextTick;
        app.config.globalProperties.CompassEnums = CompassEnums;

        app.provide('$t', app.config.globalProperties.$t);
        app.provide('$pushToast', app.config.globalProperties.$pushToast);
        app.provide('$ajax', app.config.globalProperties.$ajax);

        // Store
        app.config.globalProperties.$store = store;
        app.config.globalProperties.$storeHasModule = (path) => store._modules.root._children[path];

        // Filters (dynamic import for consistency)
        const { default: vueFilters } = await import('filters/vue_filters');
        app.config.globalProperties.$filters = vueFilters;
    }

    /**
     * Setup global references (legacy)
     * @param {App} app - The Vue instance
     */
    setupGlobalReferences(app) {
        moment.locale(window.App.lang);
        window.sellsyapp = app;
        window.sellsyapp.appContext = app._context;
        window.Vue = app;
    }

    /**
     * Execute legacy boot if configured
     */
    bootLegacy() {
        if (this.config.bootLegacy) {
            this.config.bootLegacy.call();
        }
    }

    /**
     * Initialize and boot core services
     * @param {App} app - The Vue instance
     */
    async bootCoreServices(app) {
        // Setup environment and features
        Env.set(this.bootData.config);
        Feature.set(this.bootData.features_flag);

        window.EventBus = mitt();

        // Initialize errors handler
        const errorsHandler = new ErrorsHandler();
        this.bootData.system.errorsHandler = errorsHandler;

        // Setup global store
        await Store.set(
            this.bootData.intl,
            this.bootData.user,
            this.bootData.corporation,
            this.bootData.system,
            this.bootData.subscription_features,
            this.bootData.sellsy_subscription_dictionnary,
        );

        // Boot services
        errorsHandler.boot(app);
        SellsyJS.WebServiceCaller.init();

        // Initialize SSE targets discovery
        await this.initializeTargetsDiscovery();
    }

    /**
     * Initialize SSE targets discovery system
     */
    async initializeTargetsDiscovery() {
        const { default: TargetsDiscoveryFactory } = await import('sse/TargetsDiscoveryFactory');
        TargetsDiscoveryFactory.create();
    }
}
